import type { GameServicesPlugin } from "../types/game-services";
import { PLAY_GAMES_ACHIEVEMENT_IDS } from "./playGamesAchievementIds";

const INCREMENTAL_KEYS = new Set([
  "seasoned_prospector",
  "veteran_of_the_mine",
  "nest_egg",
  "growing_reserves",
  "vault_keeper",
]);

const reportedUnlocks = new Set<string>();
const warnedMissingKeys = new Set<string>();
let signedIn = false;
let signInPromise: Promise<boolean> | null = null;
let pluginUnavailable = false;

function isAndroidCapacitor(): boolean {
  if (typeof window === "undefined") {
    if ((import.meta as any).env?.DEV) {
      console.log("[PlayGames] isAndroidCapacitor: window is undefined");
    }
    return false;
  }
  const cap = (window as any).Capacitor;
  if (!cap) {
    if ((import.meta as any).env?.DEV) {
      console.log("[PlayGames] isAndroidCapacitor: Capacitor not found");
    }
    return false;
  }
  const platform = cap.getPlatform ? cap.getPlatform() : null;
  const isAndroid = platform === "android";
  if ((import.meta as any).env?.DEV) {
    console.log(`[PlayGames] isAndroidCapacitor: platform=${platform}, isAndroid=${isAndroid}`);
  }
  return isAndroid;
}

function getGameServices(): GameServicesPlugin | null {
  if (typeof window === "undefined") {
    console.log("[PlayGames] getGameServices: window is undefined");
    return null;
  }
  const cap = (window as any).Capacitor;
  if (!cap) {
    console.log("[PlayGames] getGameServices: Capacitor not found");
    return null;
  }
  if (cap.Plugins?.GameServices) {
    console.log("[PlayGames] getGameServices: Found via cap.Plugins.GameServices");
    return cap.Plugins.GameServices as GameServicesPlugin;
  }
  if (cap.getPlugin) {
    try {
      const plugin = cap.getPlugin("GameServices") as GameServicesPlugin;
      console.log("[PlayGames] getGameServices: Found via cap.getPlugin('GameServices')");
      return plugin;
    } catch (err) {
      console.error("[PlayGames] getGameServices: Error getting plugin:", err);
      return null;
    }
  }
  console.log("[PlayGames] getGameServices: No plugin found, cap.getPlugin not available");
  return null;
}

function warnDev(message: string): void {
  if ((import.meta as any).env?.DEV) {
    console.warn(message);
  }
}

async function ensureSignedIn(): Promise<boolean> {
  if (!isAndroidCapacitor()) {
    if ((import.meta as any).env?.DEV) {
      console.log("[PlayGames] Not Android Capacitor, skipping sign-in");
    }
    return false;
  }
  if (signedIn) {
    if ((import.meta as any).env?.DEV) {
      console.log("[PlayGames] Already signed in, skipping sign-in");
    }
    return true;
  }
  if (pluginUnavailable) {
    console.log("[PlayGames] Plugin marked as unavailable, skipping sign-in");
    return false;
  }
  if (signInPromise) {
    console.log("[PlayGames] Sign-in already in progress, waiting...");
    return signInPromise;
  }

  const plugin = getGameServices();
  if (!plugin) {
    pluginUnavailable = true;
    console.error("[PlayGames] ERROR: GameServices plugin unavailable.");
    warnDev("[PlayGames] GameServices plugin unavailable.");
    return false;
  }

  console.log("[PlayGames] Starting sign-in process...");
  signInPromise = (async () => {
    try {
      console.log("[PlayGames] Calling plugin.signIn()...");
      const result = await plugin.signIn();
      console.log("[PlayGames] signIn() result:", result);
      if (result && typeof result === "object") {
        const reported = (result as any).signedIn ?? (result as any).isSignedIn;
        signedIn = reported === undefined ? true : Boolean(reported);
        console.log(`[PlayGames] Sign-in result: signedIn=${signedIn}, reported=${reported}`);
      } else {
        signedIn = true;
        console.log("[PlayGames] Sign-in succeeded (no explicit result object)");
      }
      return signedIn;
    } catch (err) {
      signedIn = false;
      console.error(`[PlayGames] ERROR: Silent sign-in failed: ${String(err)}`, err);
      warnDev(`[PlayGames] Silent sign-in failed: ${String(err)}`);
      return false;
    } finally {
      signInPromise = null;
    }
  })();

  return signInPromise;
}

export function isIncrementalKey(key: string): boolean {
  return INCREMENTAL_KEYS.has(key);
}

/**
 * Reset sign-in state - call this when account/profile changes
 * This forces re-authentication with the new account
 */
export function resetSignInState(): void {
  console.log("[PlayGames] Resetting sign-in state (account may have changed)");
  signedIn = false;
  signInPromise = null;
  // Don't reset pluginUnavailable - if plugin was unavailable, it still is
}

export async function initPlayGames(): Promise<void> {
  if ((import.meta as any).env?.DEV) {
    console.log("[PlayGames] initPlayGames() called");
  }
  const result = await ensureSignedIn();
  if ((import.meta as any).env?.DEV) {
    console.log(`[PlayGames] initPlayGames() completed, signedIn=${result}`);
  }
}

export function isPlayGamesSignedIn(): boolean {
  return signedIn;
}

export async function unlock(key: string): Promise<void> {
  if ((import.meta as any).env?.DEV) {
    console.log(`[PlayGames] unlock() called for key: ${key}`);
  }
  if (!isAndroidCapacitor()) {
    if ((import.meta as any).env?.DEV) {
      console.log("[PlayGames] Not Android, skipping unlock");
    }
    return;
  }
  if (isIncrementalKey(key)) {
    console.log(`[PlayGames] Key ${key} is incremental, skipping unlock (use increment instead)`);
    return;
  }
  const achievementId = PLAY_GAMES_ACHIEVEMENT_IDS[key];
  if (!achievementId) {
    if (!warnedMissingKeys.has(key)) {
      warnedMissingKeys.add(key);
      console.warn(`[PlayGames] Missing Google Play ID for achievement: ${key}`);
      warnDev(`[PlayGames] Missing Google Play ID for achievement: ${key}`);
    }
    return;
  }
  if (reportedUnlocks.has(key)) {
    console.log(`[PlayGames] Achievement ${key} already reported, skipping`);
    return;
  }
  const signedInResult = await ensureSignedIn();
  console.log(`[PlayGames] Sign-in check result: ${signedInResult}`);
  if (!signedInResult) {
    console.error(`[PlayGames] ERROR: Not signed in, cannot unlock ${key}`);
    return;
  }

  const plugin = getGameServices();
  if (!plugin) {
    console.error("[PlayGames] ERROR: Plugin unavailable, cannot unlock");
    return;
  }
  try {
    console.log(`[PlayGames] Calling unlockAchievement for ${key} (ID: ${achievementId})`);
    await plugin.unlockAchievement({ achievementId });
    reportedUnlocks.add(key);
    console.log(`[PlayGames] SUCCESS: unlockAchievement called for ${key}`);
  } catch (err) {
    console.error(`[PlayGames] ERROR: Failed to unlock ${key}: ${String(err)}`, err);
    warnDev(`[PlayGames] Failed to unlock ${key}: ${String(err)}`);
  }
}

export async function increment(key: string, steps = 1): Promise<void> {
  if ((import.meta as any).env?.DEV) {
    console.log(`[PlayGames] increment() called for key: ${key}, steps: ${steps}`);
  }
  if (!isAndroidCapacitor()) {
    if ((import.meta as any).env?.DEV) {
      console.log("[PlayGames] Not Android, skipping increment");
    }
    return;
  }
  if (steps <= 0) {
    console.log(`[PlayGames] Invalid steps: ${steps}, skipping increment`);
    return;
  }
  const achievementId = PLAY_GAMES_ACHIEVEMENT_IDS[key];
  if (!achievementId) {
    if (!warnedMissingKeys.has(key)) {
      warnedMissingKeys.add(key);
      console.warn(`[PlayGames] Missing Google Play ID for achievement: ${key}`);
      warnDev(`[PlayGames] Missing Google Play ID for achievement: ${key}`);
    }
    return;
  }
  const signedInResult = await ensureSignedIn();
  console.log(`[PlayGames] Sign-in check result: ${signedInResult}`);
  if (!signedInResult) {
    console.error(`[PlayGames] ERROR: Not signed in, cannot increment ${key}`);
    return;
  }

  const plugin = getGameServices();
  if (!plugin) {
    console.error("[PlayGames] ERROR: Plugin unavailable, cannot increment");
    return;
  }
  try {
    console.log(`[PlayGames] Calling progressAchievement for ${key} (ID: ${achievementId}, steps: ${steps})`);
    await plugin.progressAchievement({ achievementId, steps });
    console.log(`[PlayGames] SUCCESS: progressAchievement called for ${key}`);
  } catch (err) {
    console.error(`[PlayGames] ERROR: Failed to increment ${key}: ${String(err)}`, err);
    warnDev(`[PlayGames] Failed to increment ${key}: ${String(err)}`);
  }
}

if ((import.meta as any).env?.DEV && typeof window !== "undefined") {
  (window as any).__debugPlayGamesAchievement = {
    initPlayGames,
    unlock,
    increment,
    resetSignInState,
  };
}
